/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The trust note's words (DESIGN-SPEC §9) for whoever is looking at a PT programme.
 */

export type TrustNoteViewer = "athlete" | "athlete-admin" | "creator" | "admin";

export interface TrustNoteProgramme {
  isPT?: boolean | null;
  boxID?: string | null;
  ownerUID?: string | null;
  ownerName?: string | null;
  CoachName?: string | null;
  subscribers?: string[] | null;
  previousOwnerUID?: string | null;
}

/**
 * Only the people the PT read rule admits get a note; anyone else it lets in
 * (a platform admin) sees none rather than words written for someone else.
 */
export const resolveTrustNoteViewer = ({
  uid,
  isBoxAdmin,
  programme,
}: {
  uid: string | null;
  /** Admin of the programme's own gym. */
  isBoxAdmin: boolean;
  programme: TrustNoteProgramme;
}): TrustNoteViewer | null => {
  if (programme.isPT !== true || !uid) return null;
  if (programme.ownerUID === uid) return "creator";
  const isSubscriber = (programme.subscribers ?? []).includes(uid);
  if (isBoxAdmin) return isSubscriber ? "athlete-admin" : "admin";
  return isSubscriber ? "athlete" : null;
};

interface CopyContext {
  coachName: string;
  subscriberCount: number;
  /** Set when the gym kept the programme after its coach left. */
  keptByGymName?: string | null;
}

export const trustNoteCopy = (
  viewer: TrustNoteViewer,
  { coachName, subscriberCount, keptByGymName }: CopyContext
): { row1: string; row2: string } => {
  const coachRow = coachName ? `Private · Coach: ${coachName}` : "Private";
  const athleteRow1 = keptByGymName ? `Private · Looked after by ${keptByGymName}` : coachRow;
  const several = subscriberCount > 1;

  switch (viewer) {
    case "creator":
      return {
        row1: "Private · Your PT programme",
        row2: `Only you, your ${several ? "athletes" : "athlete"} and the gym's admins can see this.`,
      };
    case "admin":
      return { row1: coachRow, row2: "You can see this as an admin. Other coaches can't." };
    case "athlete-admin":
      return {
        row1: athleteRow1,
        row2: several
          ? "Only the athletes on this programme, your coach and the gym's admins (including you) can see this."
          : "Only you, your coach and the gym's admins (including you) can see this.",
      };
    case "athlete":
      return {
        row1: athleteRow1,
        row2: several
          ? "Only the athletes on this programme, your coach and the gym's admins can see this."
          : "Only you, your coach and the gym's admins can see this.",
      };
  }
};

const INCLUDED = "What's included: the workouts, results and notes in this programme.";
const NOT_AFFECTED = "What's not affected: your class bookings and gym membership work as before.";

/** The "What's included?" popover. Admins get none (web-ui-spec Page 10). */
export const whoCanSee = (
  viewer: TrustNoteViewer,
  {
    coachName,
    gymName,
    subscriberCount,
  }: { coachName: string; gymName: string | null; subscriberCount: number }
): { parties: string[]; included: string; notAffected: string | null } | null => {
  const gymAdmins = gymName ? `${gymName}'s owner and admins` : "The gym's owner and admins";

  switch (viewer) {
    case "admin":
      return null;
    case "creator":
      return {
        parties: ["You", subscriberCount > 1 ? "Your athletes" : "Your athlete", gymAdmins],
        included: INCLUDED,
        notAffected: null,
      };
    case "athlete":
    case "athlete-admin":
      return {
        parties: [
          "You (and any other athlete on this programme)",
          coachName ? `Your coach, ${coachName}` : "Your coach",
          viewer === "athlete-admin" ? `${gymAdmins} (including you)` : gymAdmins,
        ],
        included: INCLUDED,
        notAffected: NOT_AFFECTED,
      };
  }
};
